import type { PublicBotConfig } from '@apaxir/shared';
import { config } from '../config';

/**
 * Fetch the public bot config. Worker returns 404 for unknown bots.
 *
 * Never throws — host pages must continue to render regardless. Any error
 * (network, 5xx, malformed JSON) surfaces as `null`; the component maps
 * null to the "bot not found" error state.
 *
 * `cacheBust` opt-in: when present, appended as `?k=` to defeat the worker's
 * 60s `Cache-Control: max-age=60` browser cache. Used by the admin's iframe
 * preview after a save so changes show immediately. Production embeds
 * pass nothing → keep the cache benefit.
 */
export async function fetchPublicBotConfig(
  botId: string,
  cacheBust?: string | null,
): Promise<PublicBotConfig | null> {
  const url = cacheBust
    ? `${config.apiUrl}/bots/${encodeURIComponent(botId)}/public?k=${encodeURIComponent(cacheBust)}`
    : `${config.apiUrl}/bots/${encodeURIComponent(botId)}/public`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      // eslint-disable-next-line no-console
      if (res.status !== 404) console.warn('[apaxir-chatbot] bot config', res.status);
      return null;
    }
    return (await res.json()) as PublicBotConfig;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[apaxir-chatbot] bot config fetch failed', err);
    return null;
  }
}
